import { useEffect, useState } from 'react';
import { getMediaUrl, parseStorageKey } from '@vibesites/ui-primitives';
import { contrastRatio, parseColor } from '../utils/luminance';

/** Minimum contrast a pixel needs against the background to count as "visible". */
const PIXEL_CONTRAST_MIN = 1.6;
/** Share of the logo's opaque pixels that must be visible for the logo to read. */
const VISIBLE_SHARE_MIN = 0.2;
/** Opaque coverage above which the image carries its own backdrop (e.g. a JPG). */
const SELF_BACKED_COVERAGE = 0.97;
const SAMPLE_SIZE = 96;

const cache = new Map<string, boolean | null>();

function analyze(img: HTMLImageElement, background: string): boolean | null {
  const bg = parseColor(background);
  if (!bg) return null;
  // SVGs without intrinsic dimensions report 0 here.
  const nw = img.naturalWidth || SAMPLE_SIZE;
  const nh = img.naturalHeight || SAMPLE_SIZE;
  const scale = Math.min(1, SAMPLE_SIZE / Math.max(nw, nh));
  const w = Math.max(1, Math.round(nw * scale));
  const h = Math.max(1, Math.round(nh * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, w, h);
  // Throws SecurityError when the image host doesn't send CORS headers.
  const { data } = ctx.getImageData(0, 0, w, h);

  let opaque = 0;
  let visible = 0;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3] / 255;
    if (a < 0.5) continue;
    opaque++;
    const px = {
      r: data[i] * a + bg.r * (1 - a),
      g: data[i + 1] * a + bg.g * (1 - a),
      b: data[i + 2] * a + bg.b * (1 - a),
    };
    if (contrastRatio(px, bg) >= PIXEL_CONTRAST_MIN) visible++;
  }
  if (opaque === 0) return false;
  if (opaque / (w * h) >= SELF_BACKED_COVERAGE) return true;
  return visible / opaque >= VISIBLE_SHARE_MIN;
}

/**
 * Samples the logo's pixels and reports whether it reads against
 * `background`. Returns `null` while loading, during SSR, or when the
 * image can't be inspected (CORS) so callers can apply their own fallback.
 */
export function useLogoVisibleOnBackground(
  url: string | null | undefined,
  background: string,
): boolean | null {
  const key = url ? `${url}|${background}` : '';
  const [result, setResult] = useState<boolean | null>(() => (key ? cache.get(key) ?? null : null));

  useEffect(() => {
    if (!url) {
      setResult(null);
      return;
    }
    if (cache.has(key)) {
      setResult(cache.get(key) ?? null);
      return;
    }
    setResult(null);
    let cancelled = false;
    const storageKey = parseStorageKey(url);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      let verdict: boolean | null = null;
      try {
        verdict = analyze(img, background);
      } catch {
        verdict = null;
      }
      cache.set(key, verdict);
      if (!cancelled) setResult(verdict);
    };
    img.onerror = () => {
      cache.set(key, null);
      if (!cancelled) setResult(null);
    };
    img.src = storageKey ? getMediaUrl(storageKey, { width: 160 }) : url;
    return () => {
      cancelled = true;
    };
  }, [url, background, key]);

  return result;
}
